import {useEffect, useMemo, useState} from 'react';
import { NavLink } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import { authService, PermissionScope } from '@/services/AuthService';
import { NavSection, useNavConfig } from './NavigationComponent';
import './Navbar.css'

type CollapsedState = Record<string, boolean>;

export const useNavigation = (navConfig: readonly NavSection[]) => {
  const token = authService.keycloak.token;

  return useMemo(() => {
    if (!authService.authenticated) return [];

    return navConfig
      .map((section) => {
        
        if (section.resource) {
          const hasPermission = authService.hasPermission(section.resource, section.scope || PermissionScope.READ);
          if (!hasPermission) return null;
        }

        if (section.condition && !section.condition()) return null;

        if (section.items) {
          const filteredItems = section.items.filter(item => {
            if (!item.resource) return true;
            return authService.hasPermission(item.resource, item.scope || PermissionScope.READ);
          });
          
          if (filteredItems.length === 0) return null;
          return { ...section, items: filteredItems };
        }

        return section;
      })
      .filter((section): section is NavSection => section !== null);
  }, [token, navConfig]);
};

export default function Navbar() {
  const visibleSections = useNavigation(useNavConfig());

  const [openSections, setOpenSections] = useState<CollapsedState>(() => {
    const saved = localStorage.getItem('navbar_state');
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    localStorage.setItem('navbar_state', JSON.stringify(openSections));
  }, [openSections]);

  const toggleSection = (id: string) => {
    setOpenSections((prev) => {
      const currentState = prev[id] ?? false;
      return {
        ...prev,
        [id]: !currentState,
      };
    });
  };

  return (
    <nav className="navbar">
      <ul className="navbar-list">
        {visibleSections.map((section) => {
          const isCollapsible = section.isCollapsible !== false;
          const isOpen = isCollapsible ? (openSections[section.header] ?? false) : true;
          
          return (
            <li key={section.header} className="navbar-section">

              {isCollapsible ? (
                <div>
                  <div
                  className="navbar-header clickable"
                  onClick={() => toggleSection(section.header)}
                  role="button"
                  aria-expanded={isOpen}
                >
                  <div className="navbar-header-title">
                    {section.icon && <section.icon className="navbar-icon" size={16} />}
                    {section.header}
                  </div>
                  <ChevronDown
                    size={22}
                    strokeWidth={3}
                    className={`chevron-icon ${isOpen ? 'open' : ''}`}
                  />
                  </div>
                  <hr className="navbar-divider" />
                </div>
                
              ) : (
                <div className="navbar-header">
                  <div className="navbar-header-title">
                    {section.icon && <section.icon className="navbar-icon" size={16} />}
                    {section.header}
                  </div>
                </div>
              )}
              
              <div className={`navbar-submenu ${isOpen ? 'expanded' : 'collapsed'}`}>
                {section.component ? (
                  <div>
                    <section.component />
                    <hr className="navbar-divider" />
                  </div>
                ) : (
                  <ul className="submenu-list">
                    {section.items?.map((item) => (
                      <li key={item.label} className="navbar-item">
                        {item.route ? (
                          <NavLink to={item.route} className="navbar-button">{item.label}</NavLink>
                        ) : (
                          <a href={item.link} className="navbar-button" target="_blank" rel="noreferrer">
                            {item.label}
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </li>
          )
        })}
      </ul>
    </nav>
  );
}
